import { Factory, MonitorSmartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import type { FloorWorkstation } from '@/features/workMode/types';

interface WorkStationSetupProps {
  stations: FloorWorkstation[] | undefined;
  loading: boolean;
  onBind: (stationId: string) => void;
  canManage: boolean;
}

/**
 * First-run screen for a tablet: which station is this? The choice is kept
 * in localStorage, so it only shows again if someone clears the browser or
 * taps "Change station" on the lock screen.
 */
export function WorkStationSetup({ stations, loading, onBind, canManage }: WorkStationSetupProps) {
  const active = (stations ?? []).filter((s) => s.is_active);
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-12">
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-info-soft/40 text-info">
          <MonitorSmartphone className="h-7 w-7" />
        </span>
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Work Mode setup</p>
          <h1 className="text-3xl font-medium tracking-headline text-foreground">Which station is this tablet at?</h1>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center">
          <Factory className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-medium text-foreground">No workstations yet</p>
          {!canManage && (
            <p className="text-sm text-muted-foreground">Ask a manager to create the workstations first.</p>
          )}
          {canManage && (
            <Button asChild variant="outline" className="mt-4">
              <Link to="/mfg/workstations">Open Workstations</Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {active.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onBind(s.id)}
              className={cn(
                'flex min-h-[96px] flex-col items-start justify-center rounded-2xl border border-border bg-card px-5 py-4 text-left transition-colors',
                'hover:border-info hover:bg-info-soft/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <span className="text-lg font-medium text-foreground">{s.name}</span>
              {s.description && <span className="text-sm text-muted-foreground">{s.description}</span>}
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Not a station tablet?{' '}
        <Link to="/dashboard" className="text-info underline-offset-4 hover:underline">
          Back to Atlas
        </Link>
      </p>
    </div>
  );
}
